import i18next from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as dto from '../lib/dto';
import { ucFirst } from '../lib/string';
import { TrackFunc } from '../lib/tracking';
import ResultCard, {
  TeamResultCard,
  TeamResultCardProps,
  CharityResultCard,
  CharityResultCardProps,
  Props as ResultCardProps,
} from './ResultCard';
import { format } from '../lib/i18next';

interface Props {
  result: dto.Result;
  track: TrackFunc;
}

export default React.memo(function Result({ result, track }: Props) {
  const { t } = useTranslation();

  switch (result.Type) {
    case 'Campaign':
      return <ResultCard {...campaignResultCardProps(t, result, track)} />;
    case 'Charity':
      return (
        <CharityResultCard {...charityResultCardProps(t, result, track)} />
      );
    case 'Fundraiser':
      return <ResultCard {...fundraiserResultCardProps(result, track)} />;
    case 'LocalProject':
      return <ResultCard {...crowdfundingResultCardProps(result, track)} />;
    case 'teamsv1':
      return <TeamResultCard {...teamsResultCardProps(result, track)} />;
    case 'Page':
      return <ResultCard {...pageResultCardProps(result, track)} />;
  }
});

function campaignResultCardProps(
  t: i18next.TFunction,
  result: dto.CampaignResult,
  track: TrackFunc
): ResultCardProps {
  const subtitle =
    result.DateExpired &&
    (result.IsExpired
      ? t('resultCard.campaign.subtitle.expired', {
          defaultValue: 'Closed on {{date,date}}',
          date: result.DateExpired,
          interpolation: {
            escapeValue: false,
            format,
          },
        })
      : t('resultCard.campaign.subtitle.active', {
          defaultValue: 'Closes on {{date,date}}',
          date: result.DateExpired,
          interpolation: {
            escapeValue: false,
            format,
          },
        }));

  return {
    url: result.Link,
    img: result.Logo,
    title: result.Name,
    subtitle,
    location: result.CountryCode,
    owner: {
      name: result.CharityName,
      // No image until charity logos are indexed
    },
    onClick() {
      track({
        event: 'link click',
        subtype: 'card',
        page_section: 'campaigns',

        ...(result.CharityId && result.CharityId > 0
          ? { charity_id: result.CharityId }
          : {}),

        content_type: 'campaign_id',
        content_id: result.Id || result.CampaignGuid,
      });
    },
  };
}

function charityResultCardProps(
  t: i18next.TFunction,
  result: dto.CharityResult,
  track: TrackFunc
): CharityResultCardProps {
  return {
    url: result.Link,
    img: result.Logo,
    imgAlt: t('resultCard.charity.logoAlt', {
      defaultValue: 'Logo for {{ charity }}',
      charity: result.Name,
    }),
    title: result.Name,
    subtitle:
      result.ProfileWhat && result.ProfileWhy
        ? t('resultCard.charity.subtitle.profileWhatWhy', {
            defaultValue: 'We {{what}} to {{why}}',
            what: result.ProfileWhat,
            why: result.ProfileWhy,
          })
        : result.Description,
    location: result.CountryCode,
    onClick() {
      track({
        event: 'link click',
        subtype: 'card',
        page_section: 'charities',
        charity_id: result.Id,
        content_type: 'charity_id',
        content_id: result.Id,
      });
    },
  };
}

function teamsResultCardProps(
  result: dto.TeamResult,
  track: TrackFunc
): TeamResultCardProps {
  return {
    memberCount: result.MemberCount,
    url: result.Link,
    img: result.Logo,
    title: result.Name,
    location: result.CountryCode,
    owner: {
      name: result.CharityName,
    },
    onClick() {
      track({
        event: 'link click',
        subtype: 'card',
        page_section: 'teams',
      });
    },
  };
}

function crowdfundingResultCardProps(
  result: dto.CrowdfundingResult,
  track: TrackFunc
): ResultCardProps {
  return {
    url: result.Link,
    img: result.Logo,
    title: ucFirst(result.PitchFor),
    location: result.CountryCode,
    owner: {
      name: result.OwnerFullName,
      // No image until owner avatars are indexed
    },
    onClick() {
      track({
        event: 'link click',
        subtype: 'card',
        page_section: 'crowdfunding pages',
        content_type: 'crowdfunding_guid',
        content_id: result.Id,
      });
    },
  };
}

function fundraiserResultCardProps(
  result: dto.FundraiserResult,
  track: TrackFunc
): ResultCardProps {
  return {
    url: result.Link,
    img: result.Logo,
    imgAspectRatio: 'fundraising',
    title: result.Name,
    event: result.Subtext,
    location: result.CountryCode,
    owner: {
      name: result.OwnerFullName,
      // No image until owner avatars are indexed
    },
    onClick() {
      track({
        event: 'link click',
        subtype: 'card',
        page_section: 'fundraisers',
        charity_id: result.CharityId,
        content_type: 'fundraising_page_id',
        content_id: result.Id,
      });
    },
  };
}

function pageResultCardProps(
  result: dto.PageResult,
  track: TrackFunc
): ResultCardProps {
  return {
    url: result.Link,
    img: result.Logo,
    imgAspectRatio: 'fundraising',
    title: result.Name,
    subtitle: result.Description,
    location: 'GB', //TODO: have country code inside search - result.CountryCode,
    owner: {
      name: result.OwnerFullName,
    },
    onClick() {
      track({
        event: 'link click',
        subtype: 'card',
        page_section: 'pages',
        content_type: 'birthday_page',
        content_id: result.Id,
      });
    },
  };
}
